import React, { useRef, useEffect, useState } from 'react';
import { RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

interface PreviewPanelProps {
  htmlContent?: string;
  phpUrl?: string;
  type: 'html' | 'php' | null;
  onRefresh: () => void;
  loading?: boolean;
  error?: string | null;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  htmlContent,
  phpUrl,
  type,
  onRefresh,
  loading = false,
  error = null,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    if (type === 'html' && htmlContent && iframeRef.current) {
      try {
        const iframe = iframeRef.current;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        
        if (doc) {
          doc.open();
          doc.write(htmlContent);
          doc.close();
          setIframeError(false);
        }
      } catch (error) {
        console.error('Error updating iframe:', error);
        setIframeError(true);
      }
    }
  }, [htmlContent, type]);

  const handleIframeError = () => {
    setIframeError(true);
  };

  const openInNewTab = () => {
    if (type === 'php' && phpUrl) {
      window.open(phpUrl, '_blank');
    } else if (type === 'html' && htmlContent) {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium text-white">Preview</h3>
          {type && (
            <span className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded uppercase">
              {type}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1 text-gray-300 hover:text-white hover:bg-gray-700 rounded"
            title="Refresh preview"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          
          {(htmlContent || phpUrl) && (
            <button
              onClick={openInNewTab}
              className="p-1 text-gray-300 hover:text-white hover:bg-gray-700 rounded"
              title="Open in new tab"
            >
              <ExternalLink size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="spinner" />
              <span>Loading preview...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center text-gray-600">
              <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
              <h4 className="text-lg font-medium mb-2">Preview Error</h4>
              <p className="text-sm">{error}</p>
              <button
                onClick={onRefresh}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {type === 'html' && (
              <>
                {iframeError ? (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <div className="text-center text-gray-600">
                      <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
                      <h4 className="text-lg font-medium mb-2">Preview Error</h4>
                      <p className="text-sm">Unable to render HTML content</p>
                      <button
                        onClick={() => {
                          setIframeError(false);
                          onRefresh();
                        }}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                ) : (
                  <iframe
                    ref={iframeRef}
                    className="w-full h-full border-none"
                    title="HTML Preview"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    onError={handleIframeError}
                  />
                )}
              </>
            )}

            {type === 'php' && phpUrl && (
              <iframe
                src={phpUrl}
                className="w-full h-full border-none"
                title="PHP Preview"
                sandbox="allow-scripts allow-same-origin allow-forms"
                onError={handleIframeError}
              />
            )}

            {!type && (
              <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-center text-gray-500">
                  <div className="text-6xl mb-4">👁️</div>
                  <h4 className="text-lg font-medium mb-2">No Preview Available</h4>
                  <p className="text-sm">
                    Open an HTML or PHP file to see the preview
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PreviewPanel;